import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useScrollTrigger,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { BRAND } from '../../lib/brand';

interface Props {
  window?: () => Window;
}

const NAV_LINKS = [
  { label: 'Free Tools', to: '/tools' },
  { label: 'Incidents', to: '/incidents' },
  { label: 'Threats', to: '/threats' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/faq' },
];

const PublicHeader: React.FC<Props> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // Belt-and-braces: each nav item already closes the drawer on click, but
  // tying it to the route itself means it can never get stuck open after a
  // navigation, regardless of click/unmount timing.
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Elevation on scroll
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: props.window ? props.window() : undefined,
  });

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: trigger ? 'divider' : 'transparent'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* Logo/Brand */}
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'text.primary',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box component="img" src="/logo-mark.svg" alt="" sx={{ width: 26, height: 26 }} />
            {BRAND.productName}
          </Typography>

          {/* Navigation Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isMobile && (
              <>
                {NAV_LINKS.map((link) => (
                  <Button key={link.to} component={RouterLink} to={link.to} color="inherit" sx={{ color: 'text.secondary' }}>
                    {link.label}
                  </Button>
                ))}
              </>
            )}

            {!isMobile && (
              <Button
                color="inherit"
                sx={{ color: 'text.secondary' }}
                onClick={() => navigate('/login')}
              >
                Log In
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => navigate('/signup')}
            >
              Sign Up
            </Button>

            {isMobile && (
              <IconButton
                aria-label="Open navigation menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260, bgcolor: 'background.default' } }}
      >
        <List sx={{ pt: 3 }}>
          {NAV_LINKS.map((link) => (
            <ListItemButton
              key={link.to}
              component={RouterLink}
              to={link.to}
              onClick={() => setDrawerOpen(false)}
            >
              <ListItemText primary={link.label} />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 1 }} />
          <ListItemButton
            onClick={() => {
              setDrawerOpen(false);
              navigate('/login');
            }}
          >
            <ListItemText primary="Log In" />
          </ListItemButton>
        </List>
      </Drawer>
    </AppBar>
  );
};

export default PublicHeader;
